/* ── RowCarousel — DOM horizontal carousel ──────────────────────────────────
   A straight, DOM-based row scroller (native overflow scroll + snap, prev/next
   arrows, and mouse drag-to-scroll). The arrows are flex siblings of the scroll
   track rather than absolute overlays, so they sit OUTSIDE the scrolling area
   and never cover a tile or an inter-tile gap. Unlike the WebGL CircularGallery,
   it uses ordinary DOM children, so cross-origin thumbnails render fine (no
   CORS) and tiles keep whatever size the caller gives them.

   Children are the tiles (caller controls their size/appearance). A drag is
   distinguished from a click so dragging the row never fires a tile's onClick. */
import { Children, useCallback, useEffect, useRef, useState } from 'react';
import { AnimatedChevronLeft, AnimatedChevronRight } from './AnimatedIcons';
import './RowCarousel.css';

export default function RowCarousel({
  children, gap = 10, className = '', onReachEnd,
}) {
  const trackRef = useRef(null);
  const reachedEnd = useRef(false);
  const drag = useRef({
    down: false, startX: 0, scrollLeft: 0, moved: false,
  });
  // Where the row was resting before a hover-reveal nudged it (see revealTile).
  const hoverRestore = useRef(null);
  // A reveal scrolls tiles UNDER a stationary pointer, and every tile that
  // slides beneath it fires its own mouseover — which used to chain into another
  // reveal, walking the row along and leaving the next tile half in view. While
  // this lock is set, only real pointer movement can start another reveal.
  const revealLock = useRef(null);
  // An arrow is only meaningful when there is somewhere to scroll in that
  // direction. A row holding a single tile used to show both chevrons even
  // though they did nothing. The buttons keep their slot in the layout when
  // hidden — the section headings and the first tile are aligned to the arrow's
  // width — so this only turns their visibility/hit-testing off.
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const childCount = Children.count(children);
  const syncArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    // Sub-pixel track widths make scrollWidth/clientWidth differ by a fraction
    // on rows that don't actually overflow, so compare with a 1px tolerance.
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 1);
    setCanNext(max > 1 && el.scrollLeft < max - 1);
  }, []);

  // Fire onReachEnd once when the track scrolls near its right edge (re-arms
  // after scrolling back). Used to page in more videos, like Swiper's onReachEnd.
  const handleScroll = () => {
    syncArrows();
    const el = trackRef.current;
    if (!el || !onReachEnd) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 96;
    if (nearEnd && !reachedEnd.current) {
      reachedEnd.current = true;
      onReachEnd();
    } else if (!nearEnd && reachedEnd.current) {
      reachedEnd.current = false;
    }
  };

  useEffect(() => {
    syncArrows();
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // Re-check whenever the row or one of its tiles is resized — responsive
    // widths, a thumbnail settling, or more videos paged in.
    const ro = new ResizeObserver(syncArrows);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child));
    return () => ro.disconnect();
  }, [syncArrows, childCount]);

  const scrollByPage = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    hoverRestore.current = null;
    el.style.scrollSnapType = '';
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    drag.current = {
      down: true, startX: e.clientX, scrollLeft: el.scrollLeft, moved: false,
    };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    const el = trackRef.current;
    if (!el) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.scrollLeft - dx;
  };
  const endDrag = () => { drag.current.down = false; };

  // ── Hover-reveal for edge tiles ──────────────────────────────────────────
  // A tile that is only half inside the track still lifts/zooms on hover, but
  // the track clips it, so the hovered tile reads as sliced off (worst at the
  // right edge, where the next tile is always partly out of view). Nudge the
  // row just far enough to bring the hovered tile fully into view, and put the
  // row back where it was once the pointer leaves. Pointer-driven only, so
  // touch devices (no hover) are untouched.
  const pointer = useRef({ x: 0, y: 0 });
  const canHover = () => typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(hover: hover)').matches;

  // The tile is a direct child of the track; the event fires on whatever is
  // nested inside it.
  const tileOf = (node) => {
    const el = trackRef.current;
    if (!el) return null;
    let n = node;
    while (n && n !== el && n.parentNode !== el) n = n.parentNode;
    return n && n !== el ? n : null;
  };

  const revealTile = (tile) => {
    const el = trackRef.current;
    if (!el || !tile) return;
    // Flush to the track edge, NOT inset: the following tile starts one `gap`
    // later, so any padding here is exactly how much of it peeks back into view.
    const pad = 0;
    // Measured against the track's own box, NOT via offsetLeft: .rc-track isn't
    // positioned, so a tile's offsetParent is .row-carousel and its offsetLeft
    // carries the prev arrow's 44px (40px button + 4px margin). That constant
    // over-scrolled every reveal by 44px, which is the sliver of the next tile
    // that kept showing up at the right edge.
    const trackLeft = el.getBoundingClientRect().left;
    const rect = tile.getBoundingClientRect();
    const left = el.scrollLeft + (rect.left - trackLeft);
    const right = el.scrollLeft + (rect.right - trackLeft);
    const viewLeft = el.scrollLeft;
    const viewRight = viewLeft + el.clientWidth;
    const max = el.scrollWidth - el.clientWidth;
    let target = null;
    if (right + pad > viewRight) {
      target = Math.min(right + pad - el.clientWidth, max);
    } else if (left - pad < viewLeft) {
      target = Math.max(left - pad, 0);
    }
    if (target === null || Math.abs(target - viewLeft) < 1) return;
    // Remember only the position the row was resting at BEFORE the first
    // reveal, so moving across several edge tiles still returns to one place.
    if (hoverRestore.current === null) hoverRestore.current = viewLeft;
    // The reveal aligns the tile's right edge, which is not a snap position —
    // proximity snapping would drag it back to the nearest tile start.
    el.style.scrollSnapType = 'none';
    revealLock.current = { tile, x: pointer.current.x, y: pointer.current.y };
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  // Real movement of the mouse itself releases the lock; the scroll-induced
  // mouseovers carry the same coordinates, so they never do.
  const onTrackMouseMove = (e) => {
    pointer.current = { x: e.clientX, y: e.clientY };
    const lock = revealLock.current;
    if (lock && (Math.abs(e.clientX - lock.x) > 3 || Math.abs(e.clientY - lock.y) > 3)) {
      revealLock.current = null;
    }
  };

  const restoreScroll = () => {
    const el = trackRef.current;
    if (!el || hoverRestore.current === null) return;
    const to = hoverRestore.current;
    hoverRestore.current = null;
    revealLock.current = null;
    el.scrollTo({ left: to, behavior: 'smooth' });
    // Snapping stays off until the restore settles, or it fights the animation.
    window.setTimeout(() => {
      if (hoverRestore.current === null && trackRef.current) {
        trackRef.current.style.scrollSnapType = '';
      }
    }, 420);
  };

  // Any deliberate scroll of their own wins — drop the remembered position so
  // the row doesn't jump back to it when the pointer leaves.
  const dropRestore = () => {
    hoverRestore.current = null;
    revealLock.current = null;
    if (trackRef.current) trackRef.current.style.scrollSnapType = '';
  };

  return (
    <div className={`row-carousel ${className}`}>
      <button
        type="button"
        className={`rc-arrow rc-prev${canPrev ? '' : ' rc-arrow-off'}`}
        onClick={() => scrollByPage(-1)}
        aria-label="Scroll left"
        aria-hidden={!canPrev}
        tabIndex={canPrev ? 0 : -1}
      >
        <AnimatedChevronLeft size={22} />
      </button>
      <div
        ref={trackRef}
        className="rc-track"
        style={{ gap }}
        onScroll={handleScroll}
        onPointerDown={(e) => { dropRestore(); onPointerDown(e); }}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onWheel={dropRestore}
        onMouseMove={onTrackMouseMove}
        onMouseOver={(e) => {
          if (!canHover() || drag.current.down) return;
          const tile = tileOf(e.target);
          if (revealLock.current && revealLock.current.tile !== tile) return;
          revealTile(tile);
        }}
        onFocus={(e) => { if (canHover()) revealTile(tileOf(e.target)); }}
        onMouseLeave={restoreScroll}
        onPointerLeave={endDrag}
        // Swallow the click that follows a drag so tiles don't open mid-swipe.
        onClickCapture={(e) => {
          if (drag.current.moved) { e.stopPropagation(); e.preventDefault(); }
        }}
      >
        {children}
      </div>
      <button
        type="button"
        className={`rc-arrow rc-next${canNext ? '' : ' rc-arrow-off'}`}
        onClick={() => scrollByPage(1)}
        aria-label="Scroll right"
        aria-hidden={!canNext}
        tabIndex={canNext ? 0 : -1}
      >
        <AnimatedChevronRight size={22} />
      </button>
    </div>
  );
}
